import { PlayerPhotos, ScheduleMatchup } from '@/entities';
import { PlayerPhoto } from '@/entities/TeamPlayerInfoData';

export const determinePlayerPhotoUrl = (photo: PlayerPhoto) => {
	return photo?.systemPhoto ? photo.systemPhoto.url : photo?.userPhoto ? photo.userPhoto.url : '';
};

export const determinePlayerPhotoForPlayerMovesUrl = (photo?: PlayerPhotos) => {
	if (!photo) return undefined;
	const keys: (keyof PlayerPhotos)[] = [
		'headshot',
		'fullImage',
		'featuredImage',
		'overviewImage',
		'profileImage',
		'headerImage',
		'featuredUserImage',
		'actionUserImage'
	];

	for (const key of keys) {
		if (photo[key]?.url) {
			return photo[key]!.url;
		}
	}

	return undefined;
};

export const getPlayerOperationErrorMessage = (errorMessage: string, operation: 'move' | 'trade' | 'drop' | 'add' | 'replace'): string => {
	const operationSpecificErrors = {
		[`cannot ${operation} player to a non-existent team`]: 'the team may be deleted or associated with a different organization or team league',
		[`cannot ${operation} players from a non-existent team`]:
			'the team may be deleted or associated with a different organization or team league',
		[`cannot ${operation} players to a non-existent team`]: 'the team may be deleted or associated with a different organization or team league',
		[`cannot ${operation} players in a non-existent team`]: 'the team may be deleted or associated with a different organization or team league',

		[`cannot ${operation} player to team: team is currently disabled`]: `cannot ${operation} players to a disabled team`,
		[`cannot ${operation} players from team: team is currently disabled`]: `cannot ${operation} players from a disabled team`,
		[`cannot ${operation} players to a team: team is currently disabled`]: `cannot ${operation} players to a disabled team`,
		[`cannot ${operation} players in team: team is currently disabled`]: `cannot ${operation} players in a disabled team`,

		[`cannot ${operation} non-existent player to team`]: 'the player may be deleted or associated with a different organization or team league',
		[`cannot ${operation} non-existent player`]: 'the player may be deleted or associated with a different organization or team league',

		[`cannot ${operation} male player`]: 'the team has already reached the maximum allowed number of male players',
		[`cannot ${operation} female player`]: 'the team has already reached the maximum allowed number of female players',
		[`cannot ${operation} male players: the team has already reached the maximum allowed number of male players`]:
			'the team has already reached the maximum allowed number of male players',
		[`cannot ${operation} female player: the team has already reached the maximum allowed number of female players`]:
			'the team has already reached the maximum allowed number of female players',

		[`cannot ${operation} male player: the team already has minimum required male players`]:
			'the team already has the minimum required number of male players',
		[`cannot ${operation} female player: the team already has minimum required female players`]:
			'the team already has the minimum required number of female players'
	};

	const commonErrors = {
		'player is already a member of provided team': 'the player is already a member of the provided team',
		'player is currently disabled': 'the player is currently disabled',
		'player is already registered in a team within the same division':
			'the player is already registered in another team within the same division',
		'player is not a member of provided team': 'the player is not a member of the provided team',
		'player gender not specified': 'player gender is not specified',
		'duplicated request': 'a similar request already exists for this player and team',

		'(required: M)': 'player gender does not match division requirements (required: Male)',
		'(required: F)': 'player gender does not match division requirements (required: Female)',

		'non-existent gender type': 'the gender type is not valid',

		'rating too low': 'player rating is too low for this division',
		'rating too high': 'player rating is too high for this division',
		'age too young': 'player is too young for this division',
		'age too old': 'player is too old for this division',
		'new team rating to high': 'adding this player would exceed the maximum combined team rating'
	};

	const errorMappings = {
		...operationSpecificErrors,
		...commonErrors
	};

	for (const [key, value] of Object.entries(errorMappings)) {
		if (errorMessage.includes(key)) {
			return value;
		}
	}

	const defaultMessages = {
		add: 'Something went wrong and we were not able to add the player to the team',
		move: 'Something went wrong and we were not able to move the player to another team',
		trade: 'Something went wrong and we were not able to trade players between teams',
		drop: 'Something went wrong and we were not able to drop the player from the team',
		replace: 'Something went wrong and we were not able to replace the player'
	};

	return defaultMessages[operation] || 'Something went wrong and we were not able to perform the operation';
};

export const getSingleDistrictTitle = (systemMatchups: ScheduleMatchup[]): string | null => {
	if (!systemMatchups || systemMatchups.length === 0 || !systemMatchups[0]) return null;

	const firstTitle = systemMatchups[0].districtTitle;
	if (!firstTitle) return null;

	const isUniform = systemMatchups.every((matchup) => matchup?.districtTitle === firstTitle);

	return isUniform ? firstTitle : 'All Districts';
};

export const getSingleDivisionTitle = (systemMatchups: ScheduleMatchup[]): string | null => {
	if (!systemMatchups || systemMatchups.length === 0 || !systemMatchups[0]) return null;

	const firstTitle = systemMatchups[0].divisionTitle;
	if (!firstTitle) return null;

	const isUniform = systemMatchups.every((matchup) => matchup?.divisionTitle === firstTitle);

	return isUniform ? firstTitle : 'All Divisions';
};
